import { create } from 'zustand';
import textbookService from '../services/textbookService';

// 创建随机选择页面的状态管理
const useRandomSelectionStore = create((set, get) => ({
  // 年级和学期选择
  grade: '三年级',
  term: '全部',
  
  // 难度级别 (1-5)
  difficulty: 3,
  
  // 单词数量
  wordCount: 20,
  
  // 听写设置 - 注意：这里只保留页面特定的设置，全局设置通过useSettings获取
  dictationSettings: {
    randomOrder: true, // 随机顺序，这是随机选择页面特有的设置
    repeatCount: 3,
    repeatInterval: 3,
    wordInterval: 5
  },
  
  // 模态框状态
  modal: {
    isOpen: false,
    type: null, // 'grade', 'term'
  },
  
  // 模态框选项 - 从统一数据源获取
  modalOptions: {
    grade: [...textbookService.getGrades(), '全部'],
    term: ['上册', '下册', '全部'],
  },
  
  // 操作方法
  setGrade: (grade) => set((state) => {
    // 更新可用学期列表
    const availableTerms = grade === '全部' 
      ? ['上册', '下册', '全部'] 
      : [...textbookService.getTerms(grade), '全部'];
    
    return { 
      grade,
      modalOptions: {
        ...state.modalOptions,
        term: availableTerms
      }
    };
  }),
  
  setTerm: (term) => set({ term }),
  
  // 设置难度
  setDifficulty: (difficulty) => set({ 
    difficulty: Math.max(1, Math.min(5, difficulty)) 
  }),
  
  // 设置单词数量
  setWordCount: (count) => set({ 
    wordCount: Math.max(5, Math.min(30, count)) 
  }),
  
  // 获取随机单词
  getRandomWords: () => {
    const state = get();
    const gradeFilter = state.grade === '全部' ? null : state.grade;
    const termFilter = state.term === '全部' ? null : state.term;
    
    // 从数据源获取符合条件的所有单词
    let allWords = textbookService.getAllWords(gradeFilter, termFilter);
    
    // 按难度过滤
    if (state.difficulty > 0) {
      allWords = allWords.filter(word => 
        word.difficulty <= state.difficulty
      );
    }
    
    // 如果没有符合条件的单词，返回空数组
    if (allWords.length === 0) return [];
    
    // 随机选择指定数量的单词
    const randomWords = [];
    const wordCount = Math.min(state.wordCount, allWords.length);
    
    // 复制一份单词列表，避免修改原数组
    const wordsCopy = [...allWords];
    
    for (let i = 0; i < wordCount; i++) {
      // 随机选择一个索引
      const randomIndex = Math.floor(Math.random() * wordsCopy.length);
      // 添加到结果数组
      randomWords.push(wordsCopy[randomIndex]);
      // 从复制的数组中移除已选单词，避免重复
      wordsCopy.splice(randomIndex, 1);
    }
    
    return randomWords;
  },
  
  // 听写设置相关方法
  increaseRepeatCount: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      repeatCount: Math.min(state.dictationSettings.repeatCount + 1, 5)
    }
  })),
  
  decreaseRepeatCount: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      repeatCount: Math.max(state.dictationSettings.repeatCount - 1, 1)
    }
  })),
  
  increaseRepeatInterval: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      repeatInterval: Math.min(state.dictationSettings.repeatInterval + 1, 10)
    }
  })),
  
  decreaseRepeatInterval: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      repeatInterval: Math.max(state.dictationSettings.repeatInterval - 1, 1)
    }
  })),
  
  increaseWordInterval: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      wordInterval: Math.min(state.dictationSettings.wordInterval + 1, 10)
    }
  })),
  
  decreaseWordInterval: () => set((state) => ({
    dictationSettings: {
      ...state.dictationSettings,
      wordInterval: Math.max(state.dictationSettings.wordInterval - 1, 2)
    }
  })),
  
  // 打开模态框
  openModal: (type) => set((state) => { 
    // 如果是打开年级选择，更新可用年级列表
    if (type === 'grade') {
      return {
        modal: { isOpen: true, type },
        modalOptions: {
          ...state.modalOptions,
          grade: [...textbookService.getGrades(), '全部']
        }
      };
    }
    // 如果是打开学期选择，更新可用学期列表
    else if (type === 'term') {
      const availableTerms = state.grade === '全部' 
        ? ['上册', '下册', '全部'] 
        : [...textbookService.getTerms(state.grade), '全部'];
      
      return {
        modal: { isOpen: true, type },
        modalOptions: {
          ...state.modalOptions,
          term: availableTerms
        }
      };
    }
    // 其他类型模态框
    return { 
      modal: { isOpen: true, type }
    };
  }),
  
  // 关闭模态框
  closeModal: () => set({ 
    modal: { 
      isOpen: false, 
      type: null 
    } 
  }),
}));

export default useRandomSelectionStore;